<template>
  <div class="whs">
    <!-- Loading -->
    <div v-if="loading" class="whs-loading"><div class="whs-spinner"></div></div>

    <!-- Load error -->
    <div v-if="loadError && !loading" class="whs-error">
      <p class="whs-error-title">Could not load webhooks</p>
      <p class="whs-error-msg">{{ loadError }}</p>
      <button class="whs-btn whs-btn-outline" type="button" @click="fetchAll">Retry</button>
    </div>

    <template v-if="!loading && !loadError">
      <!-- Page header -->
      <div class="whs-page-head">
        <div class="whs-page-head-text">
          <h1 class="whs-title">Webhooks</h1>
          <p class="whs-subtitle">Receive real-time alert notifications at your own URL. Works with n8n, Make, Zapier, webhook.site, or your own server.</p>
        </div>
        <button v-if="!showForm" class="whs-btn whs-btn-primary" type="button" @click="openForm">
          <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor" style="margin-right:6px;vertical-align:-1px"><path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd"/></svg>
          Add webhook
        </button>
      </div>

      <!-- Add webhook form -->
      <div v-if="showForm" class="whs-card whs-form-card">
        <h3 class="whs-card-title">New webhook</h3>
        <div v-if="formError" class="whs-alert-error" role="alert">{{ formError }}</div>

        <div class="whs-field">
          <label class="whs-field-label">ENDPOINT URL <span class="whs-required">*</span></label>
          <input
            v-model="formUrl"
            type="url"
            class="whs-input"
            placeholder="https://hooks.example.com/sentrykit"
            autocomplete="off"
          />
          <p class="whs-field-hint">Must start with <code class="whs-code">https://</code></p>
        </div>

        <div class="whs-field">
          <label class="whs-field-label">ALERT TYPES</label>
          <label class="whs-check-row">
            <input type="checkbox" v-model="formAllTypes" class="whs-checkbox" />
            <span class="whs-check-label"><strong>All alert types</strong> — receive every alert as it fires</span>
          </label>
          <div v-if="!formAllTypes" class="whs-type-grid">
            <label v-for="t in alertTypes" :key="t" class="whs-type-chip-label" :class="{ 'active': formSelectedTypes.indexOf(t) > -1 }">
              <input type="checkbox" :value="t" v-model="formSelectedTypes" class="whs-type-hidden-cb" />
              {{ labelFor(t) }}
            </label>
          </div>
          <p v-if="!formAllTypes && formSelectedTypes.length === 0" class="whs-field-hint whs-field-hint-warn">Select at least one alert type, or enable "All alert types".</p>
        </div>

        <div class="whs-form-actions">
          <button class="whs-btn whs-btn-outline" type="button" @click="closeForm" :disabled="submitting">Cancel</button>
          <button class="whs-btn whs-btn-primary" type="button" @click="createWebhook" :disabled="submitting" :class="{ 'whs-btn-loading': submitting }">
            {{ submitting ? 'Creating…' : 'Create webhook' }}
          </button>
        </div>
      </div>

      <!-- Empty state -->
      <div v-if="!webhooks.length && !showForm" class="whs-empty">
        <svg class="whs-empty-icon" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="6" y="10" width="36" height="28" rx="3" stroke="currentColor" stroke-width="2"/>
          <path d="M6 18h36" stroke="currentColor" stroke-width="2"/>
          <path d="M14 28h8M14 33h16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          <circle cx="38" cy="38" r="8" fill="var(--bg-light)" stroke="currentColor" stroke-width="2"/>
          <path d="M35 38h6M38 35v6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
        <p class="whs-empty-title">No webhooks yet</p>
        <p class="whs-empty-sub">Add an endpoint and we'll deliver alert notifications the moment they fire.</p>
        <button class="whs-btn whs-btn-primary" type="button" @click="openForm">
          <svg width="13" height="13" viewBox="0 0 20 20" fill="currentColor" style="margin-right:5px;vertical-align:-1px"><path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd"/></svg>
          Add webhook
        </button>
      </div>

      <!-- Webhook list -->
      <div v-if="webhooks.length" class="whs-list">
        <!-- Table header -->
        <div class="whs-list-head">
          <div class="whs-col-url">Endpoint</div>
          <div class="whs-col-types">Alert types</div>
          <div class="whs-col-health">Health</div>
          <div class="whs-col-toggle">Enabled</div>
          <div class="whs-col-actions"></div>
        </div>

        <div v-for="wh in webhooks" :key="wh.id" class="whs-row-wrap">
          <!-- Auto-disabled banner -->
          <div v-if="!wh.enabled && wh.disabled_reason === 'consecutive_failures'" class="whs-banner-error" role="alert">
            <svg width="15" height="15" viewBox="0 0 20 20" fill="currentColor" style="flex-shrink:0"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>
            <span>Auto-disabled after repeated delivery failures. Fix your endpoint, then
              <button class="whs-banner-link" type="button" @click="reenableWebhook(wh)">re-enable it</button>.
            </span>
          </div>

          <!-- Main row -->
          <div class="whs-row" :class="{ 'whs-row-off': !wh.enabled, 'whs-row-expanded': expandedId === wh.id }">
            <div class="whs-col-url">
              <span class="whs-url-text" :title="wh.url">{{ truncateUrl(wh.url) }}</span>
            </div>

            <div class="whs-col-types">
              <span v-if="!wh.subscribed_alert_types.length" class="whs-chip whs-chip-all">All types</span>
              <template v-else>
                <span v-for="t in wh.subscribed_alert_types.slice(0, 2)" :key="t" class="whs-chip">{{ labelFor(t) }}</span>
                <span v-if="wh.subscribed_alert_types.length > 2" class="whs-chip whs-chip-more">+{{ wh.subscribed_alert_types.length - 2 }}</span>
              </template>
            </div>

            <div class="whs-col-health">
              <span class="whs-health" :class="healthClass(wh)">{{ healthLabel(wh) }}</span>
            </div>

            <div class="whs-col-toggle">
              <button
                type="button"
                class="whs-toggle"
                :class="{ 'whs-toggle-on': wh.enabled }"
                :disabled="togglingIds.indexOf(wh.id) > -1"
                :title="wh.enabled ? 'Disable webhook' : 'Enable webhook'"
                @click="toggleEnabled(wh)"
                :aria-pressed="wh.enabled"
              >
                <span class="whs-toggle-knob"></span>
              </button>
            </div>

            <div class="whs-col-actions">
              <button type="button" class="whs-icon-btn" :title="expandedId === wh.id ? 'Collapse' : 'Expand details'" @click="toggleExpand(wh.id)">
                <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                  <path v-if="expandedId === wh.id" fill-rule="evenodd" clip-rule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"/>
                  <path v-else fill-rule="evenodd" clip-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"/>
                </svg>
              </button>
              <button type="button" class="whs-icon-btn whs-icon-btn-danger" title="Delete webhook" @click="confirmDelete(wh.id)">
                <svg width="15" height="15" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zm-1 5a1 1 0 012 0v6a1 1 0 11-2 0V7zm4 0a1 1 0 012 0v6a1 1 0 11-2 0V7z"/>
                </svg>
              </button>
            </div>
          </div>

          <!-- Expanded detail panel -->
          <div v-if="expandedId === wh.id" class="whs-detail">
            <!-- Action bar -->
            <div class="whs-detail-actions">
              <button
                type="button"
                class="whs-btn whs-btn-outline whs-btn-sm"
                :disabled="testPending[wh.id]"
                :class="{ 'whs-btn-loading': testPending[wh.id] }"
                @click="sendTest(wh.id)"
              >
                <svg width="13" height="13" viewBox="0 0 20 20" fill="currentColor" style="margin-right:5px;vertical-align:-1px"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clip-rule="evenodd"/></svg>
                {{ testPending[wh.id] ? 'Sending…' : 'Send test event' }}
              </button>
              <button
                type="button"
                class="whs-btn whs-btn-outline whs-btn-sm"
                :disabled="rotatePending[wh.id]"
                :class="{ 'whs-btn-loading': rotatePending[wh.id] }"
                @click="rotateSecret(wh.id)"
              >
                <svg width="13" height="13" viewBox="0 0 20 20" fill="currentColor" style="margin-right:5px;vertical-align:-1px"><path fill-rule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clip-rule="evenodd"/></svg>
                {{ rotatePending[wh.id] ? 'Rotating…' : 'Rotate secret' }}
              </button>
              <span v-if="testResults[wh.id]" class="whs-test-result" :class="testResults[wh.id].ok ? 'whs-result-ok' : 'whs-result-fail'">
                <svg v-if="testResults[wh.id].ok" width="14" height="14" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/></svg>
                <svg v-else width="14" height="14" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>
                {{ testResults[wh.id].label }}
              </span>
            </div>

            <!-- Delivery log -->
            <div class="whs-delivery-wrap">
              <p class="whs-delivery-label">Recent deliveries</p>
              <div v-if="deliveriesLoading[wh.id]" class="whs-delivery-loading">
                <div class="whs-spinner whs-spinner-sm"></div> Loading…
              </div>
              <div v-else-if="deliveries[wh.id] && !deliveries[wh.id].length" class="whs-delivery-empty">
                No deliveries recorded yet. Use "Send test event" to verify your endpoint works.
              </div>
              <table v-else-if="deliveries[wh.id] && deliveries[wh.id].length" class="whs-delivery-table">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Event type</th>
                    <th>Attempt</th>
                    <th>Latency</th>
                    <th>Time</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="d in deliveries[wh.id]" :key="d.id">
                    <td>
                      <span class="whs-delivery-status" :class="d.success ? 'whs-d-ok' : 'whs-d-fail'">
                        <svg v-if="d.success" width="12" height="12" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>
                        <svg v-else width="12" height="12" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/></svg>
                        {{ d.response_code || (d.success ? '200' : 'Err') }}
                      </span>
                    </td>
                    <td>{{ labelFor(d.event_type) }}</td>
                    <td>#{{ d.attempt }}</td>
                    <td>{{ d.latency_ms != null ? d.latency_ms + 'ms' : '—' }}</td>
                    <td class="whs-delivery-time">{{ formatTime(d.attempted_at) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Signature info card -->
      <div class="whs-info-card">
        <div class="whs-info-card-head">
          <svg width="15" height="15" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M18 8a6 6 0 01-7.743 5.743L10 14l-1 1-1 1H6v2H2v-4l4.257-4.257A6 6 0 1118 8zm-6-4a1 1 0 100 2 2 2 0 012 2 1 1 0 102 0 4 4 0 00-4-4z" clip-rule="evenodd"/></svg>
          Verifying signatures in your receiver
        </div>
        <p class="whs-info-card-text">Each delivery includes <code class="whs-code">X-SentryKit-Signature: t=&lt;unix&gt;,v1=&lt;hmac&gt;</code>. Verify with <code class="whs-code">HMAC-SHA256(secret, "t.raw_body")</code> and reject timestamps older than 5 minutes.</p>
      </div>
    </template>

    <!-- ===== Signing secret modal (show-once) ===== -->
    <div v-if="secretModal.show" class="whs-overlay" role="dialog" aria-modal="true" @click.self="closeSecretModal">
      <div class="whs-modal">
        <div class="whs-modal-icon-wrap whs-modal-icon-amber">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
          </svg>
        </div>
        <h3 class="whs-modal-title">{{ secretModal.isRotation ? 'New signing secret' : 'Webhook created — save your secret' }}</h3>
        <p class="whs-modal-body">
          <strong>Copy this now — it won't be shown again.</strong> Paste it into your receiver to verify our signature.
        </p>
        <p v-if="secretModal.isRotation" class="whs-modal-note">
          Your <strong>previous secret stays valid for 24 hours</strong> so you can update your receiver without downtime.
        </p>
        <div class="whs-secret-box">
          <code class="whs-secret-code">{{ secretModal.secret }}</code>
          <button type="button" class="whs-copy-btn" @click="copyToClipboard(secretModal.secret, 'secret')">
            <svg v-if="copied !== 'secret'" width="14" height="14" viewBox="0 0 20 20" fill="currentColor"><path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z"/><path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z"/></svg>
            {{ copied === 'secret' ? '✓ Copied!' : 'Copy' }}
          </button>
        </div>
        <button type="button" class="whs-btn whs-btn-primary whs-modal-done-btn" @click="closeSecretModal">Done, I've copied it</button>
      </div>
    </div>

    <!-- ===== Delete confirm modal ===== -->
    <div v-if="deleteConfirmId != null" class="whs-overlay" role="dialog" aria-modal="true" @click.self="deleteConfirmId = null">
      <div class="whs-modal whs-modal-sm">
        <h3 class="whs-modal-title">Delete webhook?</h3>
        <p class="whs-modal-body">This permanently removes the webhook and stops all future deliveries. This cannot be undone.</p>
        <div class="whs-modal-btns">
          <button type="button" class="whs-btn whs-btn-outline" @click="deleteConfirmId = null" :disabled="deleting">Cancel</button>
          <button type="button" class="whs-btn whs-btn-danger" :disabled="deleting" :class="{ 'whs-btn-loading': deleting }" @click="deleteWebhook(deleteConfirmId)">
            {{ deleting ? 'Deleting…' : 'Delete webhook' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'WebhooksSettings',

  data: function () {
    return {
      loading: false,
      loadError: null,
      webhooks: [],
      alertTypes: [],

      showForm: false,
      formUrl: '',
      formAllTypes: true,
      formSelectedTypes: [],
      submitting: false,
      formError: null,

      togglingIds: [],
      expandedId: null,

      deliveries: {},
      deliveriesLoading: {},

      testResults: {},
      testPending: {},

      rotatePending: {},

      secretModal: { show: false, secret: '', isRotation: false },
      copied: null,

      deleteConfirmId: null,
      deleting: false
    }
  },

  mounted: function () {
    this.fetchAll()
  },

  methods: {
    fetchAll: function () {
      this.loading = true
      this.loadError = null
      var self = this
      Promise.all([
        this.$http.get('/api/v1/webhooks'),
        this.$http.get('/api/v1/webhooks/alert_types')
      ]).then(function (results) {
        self.loading = false
        self.webhooks = (results[0].body || {}).webhooks || []
        self.alertTypes = (results[1].body || {}).alert_types || []
      }).catch(function (err) {
        self.loading = false
        var body = (err && err.body) || {}
        var msg = body.message
        self.loadError = Array.isArray(msg) ? msg.join(', ') : (msg || 'Failed to load webhooks')
      })
    },

    openForm: function () {
      this.showForm = true
      this.formUrl = ''
      this.formAllTypes = true
      this.formSelectedTypes = []
      this.formError = null
    },

    closeForm: function () {
      this.showForm = false
      this.formError = null
    },

    createWebhook: function () {
      this.formError = null
      if (!this.formUrl) {
        this.formError = 'Endpoint URL is required.'
        return
      }
      if (!this.formUrl.startsWith('https://')) {
        this.formError = 'URL must start with https://'
        return
      }
      if (!this.formAllTypes && this.formSelectedTypes.length === 0) {
        this.formError = 'Select at least one alert type, or enable "All alert types".'
        return
      }
      this.submitting = true
      var self = this
      var payload = {
        webhook: {
          url: this.formUrl,
          subscribed_alert_types: this.formAllTypes ? [] : this.formSelectedTypes,
          seller_account_ids: []
        }
      }
      this.$http.post('/api/v1/webhooks', payload).then(function (res) {
        self.submitting = false
        var wh = (res.body || {}).webhook || {}
        self.webhooks.unshift(wh)
        self.closeForm()
        self.secretModal = { show: true, secret: wh.signing_secret || '', isRotation: false }
      }).catch(function (err) {
        self.submitting = false
        var body = (err && err.body) || {}
        var msg = body.message
        self.formError = Array.isArray(msg) ? msg.join(', ') : (msg || 'Failed to create webhook')
      })
    },

    toggleEnabled: function (wh) {
      var id = wh.id
      if (this.togglingIds.indexOf(id) > -1) return
      this.togglingIds.push(id)
      var self = this
      this.$http.patch('/api/v1/webhooks/' + id, { webhook: { enabled: !wh.enabled } }).then(function (res) {
        self.togglingIds = self.togglingIds.filter(function (x) { return x !== id })
        var updated = (res.body || {}).webhook
        if (updated) {
          var idx = self.webhooks.findIndex(function (w) { return w.id === id })
          if (idx > -1) self.$set(self.webhooks, idx, updated)
        }
      }).catch(function () {
        self.togglingIds = self.togglingIds.filter(function (x) { return x !== id })
      })
    },

    reenableWebhook: function (wh) {
      this.toggleEnabled({ id: wh.id, enabled: false })
    },

    confirmDelete: function (id) {
      this.deleteConfirmId = id
    },

    deleteWebhook: function (id) {
      this.deleting = true
      var self = this
      this.$http.delete('/api/v1/webhooks/' + id).then(function () {
        self.deleting = false
        self.deleteConfirmId = null
        self.webhooks = self.webhooks.filter(function (w) { return w.id !== id })
        if (self.expandedId === id) self.expandedId = null
      }).catch(function (err) {
        self.deleting = false
        var body = (err && err.body) || {}
        var msg = body.message
        self.formError = Array.isArray(msg) ? msg.join(', ') : (msg || 'Failed to delete webhook')
      })
    },

    toggleExpand: function (id) {
      if (this.expandedId === id) {
        this.expandedId = null
        return
      }
      this.expandedId = id
      if (!this.deliveries[id]) {
        this.loadDeliveries(id)
      }
    },

    loadDeliveries: function (id) {
      this.$set(this.deliveriesLoading, id, true)
      var self = this
      this.$http.get('/api/v1/webhooks/' + id + '/deliveries').then(function (res) {
        self.$set(self.deliveriesLoading, id, false)
        self.$set(self.deliveries, id, (res.body || {}).deliveries || [])
      }).catch(function () {
        self.$set(self.deliveriesLoading, id, false)
        self.$set(self.deliveries, id, [])
      })
    },

    sendTest: function (id) {
      this.$set(this.testPending, id, true)
      this.$set(this.testResults, id, null)
      var self = this
      this.$http.post('/api/v1/webhooks/' + id + '/test').then(function (res) {
        self.$set(self.testPending, id, false)
        var b = res.body || {}
        var label = b.delivered
          ? ('Delivered (' + b.status + ', ' + b.latency_ms + 'ms)')
          : ('Failed — ' + (b.error || ('HTTP ' + b.status)))
        self.$set(self.testResults, id, { ok: !!b.delivered, label: label })
      }).catch(function (err) {
        self.$set(self.testPending, id, false)
        var body = (err && err.body) || {}
        var msg = body.message
        self.$set(self.testResults, id, { ok: false, label: Array.isArray(msg) ? msg.join(', ') : (msg || 'Request failed') })
      })
    },

    rotateSecret: function (id) {
      if (!confirm('Rotate signing secret? Your old secret stays valid for 24 hours.')) return
      this.$set(this.rotatePending, id, true)
      var self = this
      this.$http.post('/api/v1/webhooks/' + id + '/rotate_secret').then(function (res) {
        self.$set(self.rotatePending, id, false)
        var wh = (res.body || {}).webhook || {}
        self.secretModal = { show: true, secret: wh.signing_secret || '', isRotation: true }
      }).catch(function (err) {
        self.$set(self.rotatePending, id, false)
        var body = (err && err.body) || {}
        alert(Array.isArray(body.message) ? body.message.join(', ') : (body.message || 'Failed to rotate secret'))
      })
    },

    closeSecretModal: function () {
      this.secretModal = { show: false, secret: '', isRotation: false }
      this.copied = null
    },

    copyToClipboard: function (text, key) {
      var self = this
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text).then(function () {
          self.copied = key
          setTimeout(function () { if (self.copied === key) self.copied = null }, 2500)
        })
      } else {
        var ta = document.createElement('textarea')
        ta.value = text
        ta.style.position = 'fixed'
        ta.style.opacity = '0'
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
        self.copied = key
        setTimeout(function () { if (self.copied === key) self.copied = null }, 2500)
      }
    },

    labelFor: function (token) {
      if (!token) return ''
      var map = {
        BUY_BOX_ISSUES: 'Buy Box Issues',
        PRICE_CHANGE: 'Price Change',
        HIJACKER_ALERT: 'Hijacker Alert',
        SUSPENDED_LISTINGS: 'Suspended Listings',
        PRODUCT_DETAILS_CHANGE: 'Product Details Change',
        FBA_FBM_STOCK: 'FBA/FBM Stock',
        FBM_STOCK: 'FBM Stock',
        SALES_VOLUME: 'Sales Volume',
        FBA_FEE_CHANGE: 'FBA Fee Change'
      }
      return map[token] || token.replace(/_/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase() })
    },

    truncateUrl: function (url) {
      if (!url) return ''
      try {
        var u = new URL(url)
        var s = u.host + u.pathname
        return s.length > 52 ? s.slice(0, 49) + '…' : s
      } catch (e) {
        return url.length > 52 ? url.slice(0, 49) + '…' : url
      }
    },

    healthClass: function (wh) {
      if (!wh.enabled && wh.disabled_reason) return 'whs-health-red'
      if (wh.consecutive_failure_count > 0) return 'whs-health-amber'
      if (wh.last_success_at) return 'whs-health-green'
      return 'whs-health-grey'
    },

    healthLabel: function (wh) {
      if (!wh.enabled && wh.disabled_reason) {
        var r = wh.disabled_reason.replace(/_/g, ' ')
        return 'Disabled (' + r + ')'
      }
      if (wh.consecutive_failure_count > 0) return 'Failing'
      if (wh.last_success_at) return 'Healthy'
      return 'No deliveries yet'
    },

    formatTime: function (iso) {
      if (!iso) return '—'
      try {
        var d = new Date(iso)
        return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      } catch (e) {
        return iso
      }
    }
  }
}
</script>

<style scoped>
/* ── Layout ── */
.whs {
  font-family: inherit;
}

.whs-page-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
}

.whs-title {
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.025em;
  color: var(--primary-navy);
  margin: 0 0 4px;
}

.whs-subtitle {
  font-size: 13px;
  color: rgb(100, 116, 136);
  margin: 0;
  max-width: 560px;
}

/* ── Loading / error ── */
.whs-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 0;
}

.whs-error {
  text-align: center;
  padding: 40px 20px;
}

.whs-error-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--primary-navy);
  margin: 0 0 6px;
}

.whs-error-msg {
  font-size: 13px;
  color: rgb(120, 130, 145);
  margin: 0 0 16px;
}

/* ── Spinner ── */
.whs-spinner {
  width: 28px;
  height: 28px;
  border: 3px solid rgb(220, 230, 240);
  border-top-color: var(--primary-navy);
  border-radius: 50%;
  animation: whs-spin 0.7s linear infinite;
}

.whs-spinner-sm {
  width: 16px;
  height: 16px;
  border-width: 2px;
  display: inline-block;
  vertical-align: middle;
  margin-right: 6px;
}

@keyframes whs-spin {
  to { transform: rotate(360deg); }
}

/* ── Buttons ── */
.whs-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 500;
  border-radius: 7px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: background 0.13s, box-shadow 0.13s, opacity 0.13s;
  white-space: nowrap;
  line-height: 1.4;
}

.whs-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.whs-btn-primary {
  background: var(--primary-navy);
  color: #fff;
  border-color: var(--primary-navy);
}

.whs-btn-primary:hover:not(:disabled) {
  background: #1a2f52;
}

.whs-btn-outline {
  background: #fff;
  color: var(--primary-navy);
  border-color: rgb(210, 220, 232);
}

.whs-btn-outline:hover:not(:disabled) {
  background: rgb(245, 248, 252);
}

.whs-btn-danger {
  background: #dc2626;
  color: #fff;
  border-color: #dc2626;
}

.whs-btn-danger:hover:not(:disabled) {
  background: #b91c1c;
}

.whs-btn-sm {
  padding: 6px 12px;
  font-size: 12px;
}

.whs-btn-loading {
  opacity: 0.65;
  cursor: wait;
}

/* ── Card ── */
.whs-card {
  background: #fff;
  border: 1px solid rgb(228, 235, 244);
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 16px;
}

.whs-form-card {
  padding: 20px 24px 24px;
  margin-bottom: 20px;
}

.whs-card-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--primary-navy);
  margin: 0 0 18px;
}

/* ── Form ── */
.whs-field {
  margin-bottom: 16px;
}

.whs-field-label {
  display: block;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.1em;
  color: rgb(100, 116, 136);
  margin-bottom: 6px;
}

.whs-required {
  color: #dc2626;
  margin-left: 2px;
}

.whs-input {
  width: 100%;
  max-width: 480px;
  padding: 9px 12px;
  font-size: 13px;
  border: 1px solid rgb(210, 220, 232);
  border-radius: 7px;
  color: var(--primary-navy);
  background: #fff;
  box-sizing: border-box;
  transition: border-color 0.13s, box-shadow 0.13s;
}

.whs-input:focus {
  outline: none;
  border-color: var(--primary-navy);
  box-shadow: 0 0 0 3px rgba(18, 46, 82, 0.08);
}

.whs-field-hint {
  font-size: 12px;
  color: rgb(130, 142, 158);
  margin: 5px 0 0;
}

.whs-field-hint-warn {
  color: #b45309;
}

.whs-alert-error {
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 7px;
  padding: 10px 14px;
  font-size: 13px;
  color: #991b1b;
  margin-bottom: 16px;
}

.whs-code {
  font-family: 'SF Mono', 'Fira Mono', monospace;
  font-size: 11.5px;
  background: rgba(18, 46, 82, 0.06);
  padding: 1px 5px;
  border-radius: 4px;
}

.whs-check-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  cursor: pointer;
  font-size: 13px;
  color: var(--primary-navy);
}

.whs-checkbox {
  width: 15px;
  height: 15px;
  flex-shrink: 0;
  cursor: pointer;
  accent-color: var(--primary-navy);
}

.whs-check-label {
  line-height: 1.4;
}

.whs-type-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.whs-type-chip-label {
  display: inline-flex;
  align-items: center;
  padding: 5px 11px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid rgb(210, 220, 232);
  border-radius: 20px;
  color: rgb(80, 96, 116);
  background: #fff;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
  user-select: none;
}

.whs-type-chip-label.active {
  background: rgb(235, 243, 255);
  border-color: var(--primary-navy);
  color: var(--primary-navy);
  font-weight: 600;
}

.whs-type-hidden-cb {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
}

.whs-form-actions {
  display: flex;
  gap: 10px;
  margin-top: 20px;
}

/* ── Empty state ── */
.whs-empty {
  text-align: center;
  padding: 52px 20px;
  background: #fff;
  border: 1px dashed rgb(210, 220, 235);
  border-radius: 10px;
}

.whs-empty-icon {
  width: 48px;
  height: 48px;
  color: rgb(180, 195, 212);
  margin: 0 auto 14px;
  display: block;
}

.whs-empty-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--primary-navy);
  margin: 0 0 6px;
}

.whs-empty-sub {
  font-size: 13px;
  color: rgb(120, 135, 155);
  margin: 0 0 20px;
}

/* ── List / rows ── */
.whs-list {
  background: #fff;
  border: 1px solid rgb(228, 235, 244);
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 20px;
}

.whs-list-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 16px;
  background: rgb(248, 250, 253);
  border-bottom: 1px solid rgb(228, 235, 244);
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.09em;
  color: rgb(120, 135, 155);
}

.whs-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 13px 16px;
  border-bottom: 1px solid rgb(238, 244, 250);
  transition: background 0.12s;
}

.whs-row:hover {
  background: rgb(250, 252, 255);
}

.whs-row-off .whs-url-text,
.whs-row-off .whs-chip {
  opacity: 0.45;
}

.whs-row-expanded {
  background: rgb(248, 251, 255);
}

.whs-row-wrap:last-child .whs-row {
  border-bottom: none;
}

/* columns */
.whs-col-url    { flex: 1; min-width: 0; }
.whs-col-types  { width: 220px; flex-shrink: 0; display: flex; flex-wrap: wrap; gap: 5px; }
.whs-col-health { width: 130px; flex-shrink: 0; }
.whs-col-toggle { width: 52px; flex-shrink: 0; }
.whs-col-actions{ width: 64px; flex-shrink: 0; display: flex; align-items: center; gap: 4px; justify-content: flex-end; }

.whs-url-text {
  font-size: 13px;
  font-weight: 500;
  color: var(--primary-navy);
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: 'SF Mono', 'Fira Mono', monospace;
  font-size: 12px;
}

/* chips */
.whs-chip {
  display: inline-block;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 500;
  border-radius: 12px;
  background: rgb(235, 243, 255);
  color: var(--primary-navy);
  white-space: nowrap;
}

.whs-chip-all {
  background: rgb(236, 253, 245);
  color: #065f46;
}

.whs-chip-more {
  background: rgb(245, 247, 250);
  color: rgb(100, 116, 136);
}

/* health */
.whs-health {
  display: inline-block;
  padding: 3px 9px;
  font-size: 11.5px;
  font-weight: 600;
  border-radius: 20px;
}

.whs-health-green { background: rgb(236, 253, 245); color: #065f46; }
.whs-health-amber { background: rgb(255, 251, 235); color: #92400e; }
.whs-health-red   { background: rgb(254, 242, 242); color: #991b1b; }
.whs-health-grey  { background: rgb(245, 247, 250); color: rgb(100, 116, 136); }

/* toggle switch */
.whs-toggle {
  width: 36px;
  height: 20px;
  border-radius: 10px;
  background: rgb(210, 220, 232);
  border: none;
  padding: 2px;
  cursor: pointer;
  transition: background 0.18s;
  position: relative;
  flex-shrink: 0;
}

.whs-toggle-on {
  background: var(--primary-navy);
}

.whs-toggle:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.whs-toggle-knob {
  display: block;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
  transition: transform 0.18s;
}

.whs-toggle-on .whs-toggle-knob {
  transform: translateX(16px);
}

/* icon buttons */
.whs-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: 1px solid rgb(220, 228, 238);
  background: #fff;
  color: rgb(100, 116, 136);
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
}

.whs-icon-btn:hover {
  background: rgb(245, 248, 252);
  color: var(--primary-navy);
}

.whs-icon-btn-danger:hover {
  background: #fef2f2;
  color: #dc2626;
  border-color: #fecaca;
}

/* ── Banner ── */
.whs-banner-error {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #fef2f2;
  border-bottom: 1px solid #fecaca;
  font-size: 12.5px;
  color: #991b1b;
}

.whs-banner-link {
  background: none;
  border: none;
  padding: 0;
  color: #dc2626;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
  font-size: inherit;
}

/* ── Detail panel ── */
.whs-detail {
  background: rgb(247, 250, 254);
  border-top: 1px solid rgb(228, 237, 248);
  padding: 16px 20px 20px;
}

.whs-detail-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 18px;
  flex-wrap: wrap;
}

.whs-test-result {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12.5px;
  font-weight: 500;
  padding: 4px 10px;
  border-radius: 6px;
}

.whs-result-ok   { background: rgb(236, 253, 245); color: #065f46; }
.whs-result-fail { background: #fef2f2; color: #991b1b; }

/* ── Delivery log ── */
.whs-delivery-label {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.09em;
  color: rgb(120, 135, 155);
  margin: 0 0 10px;
  text-transform: uppercase;
}

.whs-delivery-loading {
  font-size: 13px;
  color: rgb(120, 135, 155);
  display: flex;
  align-items: center;
}

.whs-delivery-empty {
  font-size: 13px;
  color: rgb(140, 155, 170);
  padding: 12px 0;
}

.whs-delivery-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12.5px;
}

.whs-delivery-table th {
  text-align: left;
  padding: 6px 10px 6px 0;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: rgb(120, 135, 155);
  border-bottom: 1px solid rgb(220, 230, 242);
  white-space: nowrap;
}

.whs-delivery-table td {
  padding: 8px 10px 8px 0;
  border-bottom: 1px solid rgb(232, 240, 250);
  color: var(--primary-navy);
  vertical-align: middle;
}

.whs-delivery-table tr:last-child td {
  border-bottom: none;
}

.whs-delivery-status {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-weight: 600;
  font-size: 12px;
}

.whs-d-ok   { color: #059669; }
.whs-d-fail { color: #dc2626; }

.whs-delivery-time {
  color: rgb(120, 135, 155);
  white-space: nowrap;
}

/* ── Info card ── */
.whs-info-card {
  background: rgb(248, 251, 255);
  border: 1px solid rgb(220, 232, 248);
  border-radius: 9px;
  padding: 14px 18px;
  margin-top: 8px;
}

.whs-info-card-head {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--primary-navy);
  margin-bottom: 7px;
}

.whs-info-card-text {
  font-size: 12.5px;
  color: rgb(80, 96, 116);
  margin: 0;
  line-height: 1.6;
}

/* ── Modal overlay ── */
.whs-overlay {
  position: fixed;
  inset: 0;
  background: rgba(10, 20, 40, 0.42);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
}

.whs-modal {
  background: #fff;
  border-radius: 14px;
  padding: 28px 28px 24px;
  max-width: 460px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(10, 20, 40, 0.22);
}

.whs-modal-sm {
  max-width: 380px;
}

.whs-modal-icon-wrap {
  width: 46px;
  height: 46px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
}

.whs-modal-icon-wrap svg {
  width: 22px;
  height: 22px;
}

.whs-modal-icon-amber {
  background: rgb(255, 251, 235);
  color: #92400e;
}

.whs-modal-title {
  font-size: 17px;
  font-weight: 700;
  color: var(--primary-navy);
  margin: 0 0 10px;
  text-align: center;
}

.whs-modal-body {
  font-size: 13.5px;
  color: rgb(70, 85, 105);
  margin: 0 0 12px;
  line-height: 1.6;
  text-align: center;
}

.whs-modal-note {
  font-size: 12.5px;
  color: #92400e;
  background: rgb(255, 251, 235);
  border: 1px solid rgb(253, 230, 138);
  border-radius: 7px;
  padding: 9px 13px;
  margin: 0 0 16px;
  text-align: center;
  line-height: 1.5;
}

.whs-secret-box {
  display: flex;
  align-items: center;
  gap: 0;
  background: rgb(246, 248, 252);
  border: 1px solid rgb(210, 220, 235);
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 18px;
}

.whs-secret-code {
  flex: 1;
  min-width: 0;
  padding: 11px 14px;
  font-family: 'SF Mono', 'Fira Mono', monospace;
  font-size: 11.5px;
  color: var(--primary-navy);
  word-break: break-all;
  line-height: 1.5;
}

.whs-copy-btn {
  padding: 11px 16px;
  background: var(--primary-navy);
  color: #fff;
  border: none;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: background 0.13s;
  display: flex;
  align-items: center;
  gap: 5px;
}

.whs-copy-btn:hover {
  background: #1a2f52;
}

.whs-modal-done-btn {
  width: 100%;
  justify-content: center;
  padding: 11px;
  font-size: 14px;
}

.whs-modal-btns {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 18px;
}
</style>



// WEBPACK FOOTER //
// WebhooksSettings.vue?37e84e35